import {
  Button,
  EmptyState,
  Flex,
  Loading,
  Text
} from 'onefaceui-react-components';
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import defaultFetch from 'react/api/sagas/fetch/defaultFetch';

import { useEventComments } from 'react/queries/eventComments';
import { getResourceTo } from 'react/utils/utils';
import { formatDateTime } from '../../utils/formatDateTime';
import {
  Comments,
  CommentsContent,
  CommentsInsert,
  Container,
  FlexContainer
} from './styles';

export function CommentsPanel({ eventLogId, closed }) {
  const { data = [], isFetching, refetch } = useEventComments(eventLogId);
  const [isLoading, setIsLoading] = useState(false);

  const commentsRef = useRef(null);
  const commentFieldRef = useRef(null);

  const addComment = async () => {
    const commentValue = commentFieldRef.current?.value;

    if (!commentValue || commentValue?.trim().length === 0) return;

    setIsLoading(true);

    const response = await defaultFetch(
      '/EventLogNotes',
      {
        closeException: false,
        comment: commentValue,
        eventLog: {
          id: eventLogId
        }
      },
      'POST'
    );

    if (response.error) {
      const monetMessage = new Monet.Form.Messages.InlineMessage();
      monetMessage.setScope('#eventDetailsModalWrapper');
      monetMessage.setInfoType('error');
      monetMessage.setMessage(getResourceTo('eventDetails.internal.error'));
      monetMessage.triggerMessage('error');
    }

    commentFieldRef.current.value = '';
    setIsLoading(false);
    refetch();
  };

  useEffect(() => {
    const { current } = commentsRef;

    if (current) {
      current.scrollTop = current.scrollHeight;
    }
  }, [isFetching]);

  const hasData = !isFetching && data && data.length > 0;
  const hasNoData = !isFetching && data && data.length === 0;

  return (
    <Container data-testid="event-details-comments">
      <Text size="md" className="panel-title">
        {getResourceTo('eventDetails.modal.comment.title')}
      </Text>
      <CommentsContent>
        <FlexContainer shouldShow={isFetching} justifyContent="center" alignItems="center">
          <Loading
            message={getResourceTo('loading.message')}
            isVisible
          />
        </FlexContainer>
        <Comments flexDirection="column" shouldShow={hasData} ref={commentsRef}>
          {data.map(({ comment, noteTime, user }) => (
            <div
              className="comment"
              data-testid="comment-content"
              key={`${user.id}-${noteTime}`}
            >
              <Flex justifyContent="space-between">
                <Text size="xs" weight="medium" className="title">
                  <strong>{user.name}</strong>
                  {' '}
                  {getResourceTo('eventDetails.modal.comment.added')}
                  {' '}
                  -
                  {' '}
                  {formatDateTime(noteTime)}
                </Text>
              </Flex>
              <Text size="xs" weight="medium" className="message">{comment}</Text>
            </div>
          ))}
        </Comments>
        <FlexContainer shouldShow={hasNoData} justifyContent="center" alignItems="center">
          <EmptyState type="data" message={getResourceTo('eventDetails.modal.comment.empty')} />
        </FlexContainer>
        <CommentsInsert>
          <Flex flexDirection="column">
            <label>{getResourceTo('eventDetails.modal.comment')}</label>
            <textarea
              ref={commentFieldRef}
              disabled={closed}
              data-testid="new-comment-textarea"
            />
          </Flex>
          <Flex justifyContent="flex-end">
            <Button
              onClick={addComment}
              loading={isLoading}
              variant="primary"
              type="button"
              dataTestId="new-comment-submit"
              disabled={closed}
            >
              {getResourceTo('eventDetails.modal.comment.btnAdd')}
            </Button>
          </Flex>
        </CommentsInsert>
      </CommentsContent>
    </Container>
  );
}

CommentsPanel.propTypes = {
  eventLogId: PropTypes.string.isRequired,
  closed: PropTypes.bool.isRequired
};
